import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import logo from '@assets/logo_landing_page.svg';

import { useResetPassword } from './hooks';
import LoopIcon from '@mui/icons-material/Loop';
import { Form, FormProvider } from 'react-hook-form';
import { TextFormField } from '@components';

interface ResetPasswordProps {
  validateEmail: (data: {
    email: string;
    captcha_id: string;
    captcha_answer: string;
  }) => void;
}

export const SendLink = ({ validateEmail }: ResetPasswordProps) => {
  const {
    handleReturnToLogin,
    isSubmitting,
    svg,
    handleRefresh,
    loading,
    formMethods,
    onSubmit,
  } = useResetPassword({ validateEmail });

  const { control } = formMethods;

  return (
    <FormProvider {...formMethods}>
      <Form
        control={control}
        onSubmit={e => onSubmit(e.event)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '3.5rem',
          width: '28rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <img
            src={logo}
            style={{
              objectFit: 'cover',
              height: '131px',
              width: '132px',
            }}
            alt='logo'
          />
        </Box>
        <Paper
          elevation={8}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '2rem',
            paddingLeft: '42px',
            gap: '2rem',
            width: '100%',
            borderRadius: 0,
            overflowY: 'scroll',
            overflowX: 'hidden',
            maxHeight: '70vh',
            '&::-webkit-scrollbar': {
              width: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'transparent',
              borderRadius: '10px',
            },
            '&:hover::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&:focus': {
              outline: 'none',
            },
            outline: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <RestartAltIcon sx={{ fontSize: '3rem' }} color='primary' />
            <Typography
              variant='h5'
              sx={{ fontWeight: '600', fontSize: '1.5rem', color: 'primary' }}
            >
              Reset your password
            </Typography>
            <Typography
              sx={{
                fontWeight: '400',
                fontSize: '1rem',
                textAlign: 'center',
                pt: '1.25rem',
                color: 'primary',
              }}
            >
              Enter your email address to receive a link to reset your password.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: '1rem',
            }}
          >
            <TextFormField
              label='Email'
              variant='outlined'
              type='email'
              name='email'
              helperText={
                'Use corporate email address e.g. @brigadehealth.com or @seniordoc.com'
              }
              required
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                maxWidth: '100%',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  maxWidth: '100%',
                }}
              >
                <Box
                  dangerouslySetInnerHTML={{ __html: svg }}
                  sx={{ width: 328, height: 60 }}
                />
              </Box>
              <Box
                sx={{
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <IconButton
                  sx={{
                    width: '30px',
                    height: '30px',
                    display: 'grid',
                    padding: 0,
                    placeItems: 'center',
                    ':hover': {
                      cursor: 'pointer',
                      bgcolor: theme => theme.palette.grey[50],
                      borderRadius: '6px',
                    },
                  }}
                  loading={loading}
                  onClick={handleRefresh}
                >
                  <LoopIcon />
                </IconButton>
              </Box>
            </Box>

            <TextFormField
              label='Enter the code shown above'
              variant='outlined'
              type='text'
              name='captcha_answer'
              required
              slotProps={{ htmlInput: { maxLength: 5 } }}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <Button
              variant='contained'
              type='submit'
              color='secondary'
              sx={{
                color: 'white',
                padding: '0.5rem',
                fontWeight: '500',
                width: '100%',
              }}
              disabled={isSubmitting}
            >
              RECEIVE A LINK
            </Button>
            <Button
              variant='text'
              color='secondary'
              sx={{
                color: 'primary.main',
                padding: '0.5rem',
                fontWeight: '500',
                width: '100%',
              }}
              onClick={handleReturnToLogin}
            >
              RETURN TO LOGIN
            </Button>
          </Box>
        </Paper>
      </Form>
    </FormProvider>
  );
};
